import React, { ComponentProps, useEffect, useState } from 'react';
import { useSuspenseQuery } from '@apollo/client/react';
import { usePageContext } from '../../../hooks/PageContext/usePageContext';
import useOrderTracking from '../../../hooks/useOrderTracking';
import { useSettings } from '../../../hooks/useSettings';
import trackLayoutQuery from './graphql';
import {
  GetTrackPageLayoutQuery,
  GetTrackPageLayoutQueryVariables,
} from '../../../../../__generated__/loma';
import { injectRetailMediaBelowEdd } from '../../../../../lib/retailMedia';
import * as LayoutTree from './layoutTree';
import LayoutComponent from './LayoutComponent';
import { LayoutNodeRenderers } from './renderLayout';
import useRem, { DEFAULT_REM } from './hooks/useRem';
import useWindowSize from './hooks/useWindowSize';

const DT_DEFAULT_MAX_WIDTH = '59.5rem'; // 952px
const desktopBreakPointMinWidth = 768;

export { LayoutTree, DT_DEFAULT_MAX_WIDTH, useWindowSize, useRem };

export type Breakpoints = Record<
  Exclude<LayoutTree.Devices, LayoutTree.Devices.MOBILE>,
  number
>;

export interface KitDynamicLayoutProps extends Pick<
  LayoutNodeRenderers,
  'feature'
> {
  breakpoints?: Breakpoints;
  userAgentDevice?: LayoutTree.Devices;
  deviceScreenType?: LayoutTree.Devices;
  view?: ComponentProps<typeof LayoutComponent>['view'];
  injectRetailMedia?: boolean;
}

export const DEVICE_LAYOUT_NAME = 'trackPageLayout';

function KitDynamicLayout({
  feature,
  view,
  userAgentDevice,
  deviceScreenType,
  injectRetailMedia,
}: KitDynamicLayoutProps) {
  const { state } = usePageContext();
  const {
    retailerMoniker,
    versionName,
    localeLanguage,
    localeCountry,
    cache,
    type,
  } = state;
  const { getCurrentShipment } = useOrderTracking();
  const currentShipment = getCurrentShipment();

  // allows for updating the layout in the case that the window size doesn't
  // match the user-agent device type, or on window resize.
  const { data } = useSuspenseQuery<
    GetTrackPageLayoutQuery,
    GetTrackPageLayoutQueryVariables
  >(trackLayoutQuery, {
    skip: deviceScreenType === userAgentDevice,
    variables: {
      retailerMoniker,
      versionName,
      localeLanguage,
      localeCountry,
      status: currentShipment?.status,
      category: deviceScreenType?.toUpperCase(),
      cache,
      type,
    },
  });

  const windowBasedDeviceLayout = data?.retailerSettings?.trackPageLayout;

  const { [DEVICE_LAYOUT_NAME]: deviceLayoutSettings } = useSettings();

  const resolvedDeviceLayout = windowBasedDeviceLayout ?? deviceLayoutSettings;

  const deviceLayout =
    injectRetailMedia && deviceScreenType === LayoutTree.Devices.MOBILE
      ? injectRetailMediaBelowEdd(resolvedDeviceLayout)
      : resolvedDeviceLayout;

  if (!deviceLayout) return null;

  return (
    <LayoutComponent
      deviceLayout={deviceLayout}
      feature={feature}
      view={view}
    />
  );
}

function KitDynamicLayoutWithSettings({
  breakpoints = {
    [LayoutTree.Devices.DESKTOP]: desktopBreakPointMinWidth / DEFAULT_REM,
  },
  ...props
}: ComponentProps<typeof KitDynamicLayout>) {
  const windowWidth = (useWindowSize().width || 0) / useRem();

  const possibleDevicePerWindowWidth = (): LayoutTree.Devices | undefined => {
    if (!windowWidth || !breakpoints) return undefined;
    return windowWidth >= breakpoints[LayoutTree.Devices.DESKTOP]
      ? LayoutTree.Devices.DESKTOP
      : LayoutTree.Devices.MOBILE;
  };

  // The Devices fallback order here is purposefully distinct between first render and
  // useEffect updates. LayoutComponent can be SSR'd, which means it always needs to
  // have its initial layout state first be determined by userAgentDevice. Otherwise,
  // if we prefer window width first, the initial state in the render on the server
  // can be different than the initial state in the browser, leading to initial hydration problems.
  // When we're performing subsequent updates in the browser (aka, updating from
  // the useEffect hook), we want to prefer the window width.
  const getDeviceTypeForFirstRender = (): LayoutTree.Devices =>
    props.userAgentDevice ||
    possibleDevicePerWindowWidth() ||
    LayoutTree.Devices.MOBILE;
  const getDeviceScreenTypeForBrowserUpdates = (): LayoutTree.Devices =>
    possibleDevicePerWindowWidth() ||
    props.userAgentDevice ||
    LayoutTree.Devices.MOBILE;

  const [deviceScreenType, setDeviceScreenType] = useState(
    getDeviceTypeForFirstRender(),
  );

  // we initially want to set layout based on known device
  // type or windowWidth (if already defined, as per embeds),
  // but also allow for updating this on screen resize.
  useEffect(
    () => {
      // setLayout(deviceLayout[getDeviceTypeForUseEffectUpdates()]);
      setDeviceScreenType(getDeviceScreenTypeForBrowserUpdates());
    },
    // TODO: Make this dependency array exhaustive
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [windowWidth],
  );

  // TODO: handle updating of deviceScreenType after the initial settings query
  // probably fetch trackLayout with category: deviceScreenType.toUpperCase();
  return (
    <KitDynamicLayout
      deviceScreenType={deviceScreenType}
      userAgentDevice={props.userAgentDevice}
      {...props}
    />
  );
}

export default KitDynamicLayoutWithSettings;
